import { useIsTeamsQnaUxUpdatesEnabled } from '@ms/yammer-hooks/dist/teamsMeetings';
import type { ITheme } from '@ms/yammer-web-core-ui/dist/util/theme';
import {
  defaultDarkTheme,
  defaultTheme,
  teamsMeetingDarkTheme,
  teamsMeetingTheme,
  teamsMeetingUpdatedTheme,
} from '@ms/yammer-web-core-ui/dist/util/theme';
import type { Theme as Fluent9Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import {
  teamsDarkTheme as fluent9TeamsDarkTheme,
  teamsHighContrastTheme as fluent9TeamsHighContrastTheme,
  teamsLightTheme as fluent9TeamsLightTheme,
} from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import {
  getCallId,
  getDeepLinkPath,
  getMeetingIdThrows,
  getMeetingTenantId,
  getOrganizerId,
  getQnAAppType,
  getTeamsMeetingId,
  getTheme,
  getUserId,
  getUserTenantId,
} from 'domains/teams/selectors';

import { useSelector } from './useSelector';

type UseTheme = () => ITheme;
export const useTheme: UseTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'dark' ? defaultDarkTheme : defaultTheme;
};

const useTeamsMeetingLightTheme: UseTeamsMeetingTheme = () => {
  const isTeamsQnaUxUpdatesEnabled = useIsTeamsQnaUxUpdatesEnabled();

  return isTeamsQnaUxUpdatesEnabled ? teamsMeetingUpdatedTheme : teamsMeetingTheme;
};

type UseTeamsMeetingTheme = () => ITheme;
export const useTeamsMeetingTheme: UseTeamsMeetingTheme = () => {
  const theme = useSelector(getTheme);
  const teamsMeetingLightTheme = useTeamsMeetingLightTheme();

  return theme === 'dark' ? teamsMeetingDarkTheme : teamsMeetingLightTheme;
};

type UseIsTeamsContrastTheme = () => boolean;
export const useIsTeamsContrastTheme: UseIsTeamsContrastTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'contrast';
};

type UseFluent9TeamsTheme = () => Fluent9Theme;
export const useFluent9TeamsTheme: UseFluent9TeamsTheme = () => {
  const theme = useSelector(getTheme);
  if (useIsTeamsContrastTheme()) {
    return fluent9TeamsHighContrastTheme;
  }

  return theme === 'dark' ? fluent9TeamsDarkTheme : fluent9TeamsLightTheme;
};

type UseUserId = () => ReturnType<typeof getUserId>;
export const useUserId: UseUserId = () => useSelector(getUserId);

type UseUserTenantId = () => ReturnType<typeof getUserTenantId>;
export const useUserTenantId: UseUserTenantId = () => useSelector(getUserTenantId);

type UseMeetingIdThrows = () => ReturnType<typeof getMeetingIdThrows>;
export const useMeetingIdThrows: UseMeetingIdThrows = () => useSelector(getMeetingIdThrows);

type UseOrganizerId = () => ReturnType<typeof getOrganizerId>;
export const useOrganizerId: UseOrganizerId = () => useSelector(getOrganizerId);

type UseMeetingTenantId = () => ReturnType<typeof getMeetingTenantId>;
export const useMeetingTenantId: UseMeetingTenantId = () => useSelector(getMeetingTenantId);

type UseDeepLinkPath = () => ReturnType<typeof getDeepLinkPath>;
export const useDeepLinkPath: UseDeepLinkPath = () => useSelector(getDeepLinkPath);

type UseTeamsMeetingId = () => ReturnType<typeof getTeamsMeetingId>;
export const useTeamsMeetingId: UseTeamsMeetingId = () => useSelector(getTeamsMeetingId);

type UseQnAAppType = () => ReturnType<typeof getQnAAppType>;
export const useQnAAppType: UseQnAAppType = () => useSelector(getQnAAppType);

type UseQnACallId = () => ReturnType<typeof getCallId>;
export const useQnACallId: UseQnACallId = () => useSelector(getCallId);
